import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  listProfessionGroups,
  deleteProfessionGroup,
  updateProfessionGroupStatus,
} from "../../../../actions/professionGroupActions";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Spinner } from "@chakra-ui/react";
import Table from "../../../../components/shared/Table";
import { Box } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columnsDesktop } from "../columns/ProfessionGroupListColumns"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/ProfessionGroupListColumnsMobile";
import { useTranslation } from "react-i18next";
import {
  PROFESSIONGROUP_DELETE_RESET,
  PROFESSIONGROUP_UPDATE_RESET,
  PROFESSIONGROUP_CREATE_RESET,
  PROFESSIONGROUP_UPDATE_STATUS_RESET,
} from "../../../../constants/professionGroupConstants";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import CreateProfessionGroup from "../sidepanels/CreateProfessionGroup";
// import ProfessionGroupsStats from "../partials/ProfessionGroupsStats";
import useRoleCheck from "../../../../hooks/useRoleCheck";
import { primary } from "../../../../utils/colors";
import useThemeColors from "../../../../hooks/useThemeColors";
import useDebounce from "../../../../hooks/useDebounce";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import PageHeader from "../../../../components/layout/PageHeader";
import SearchInput from "../../../../components/input/SearchInput";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
import EditProfessionGroup from "../sidepanels/EditProfessionGroup";
import useIsMobile from "../../../../hooks/useIsMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";

const ProfessionGroupListNew = () => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const isMobile = useIsMobile();

  // COLORS
  const { whiteMode } = useThemeColors();

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // ROLES
  const isAdmin = useRoleCheck("admin");

  // STATE
  const [selectedProfessionCount, setSelectedProfessionCount] = useState(0);

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  const [professionGroupId, setProfessionGroupId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [professionGroupName, setProfessionGroupName] = useState("");

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const professionGroupList = useSelector((state) => state.professionGroupList);
  const {
    loading,
    error,
    professionGroups,
    pages,
    totalCount,
    countsByStatus,
    globalTotalCount,
  } = professionGroupList;

  const professionGroupCreate = useSelector(
    (state) => state.professionGroupCreate
  );
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    professionGroup: createdProfessionGroup,
  } = professionGroupCreate;

  const professionGroupUpdate = useSelector(
    (state) => state.professionGroupUpdate
  );
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = professionGroupUpdate;

  const professionGroupDelete = useSelector(
    (state) => state.professionGroupDelete
  );

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = professionGroupDelete;

  const professionGroupUpdateStatus = useSelector(
    (state) => state.professionGroupUpdateStatus
  );
  const {
    loading: loadingUpdateStatus,
    error: errorUpdateStatus,
    success: successUpdateStatus,
  } = professionGroupUpdateStatus;

  const professionGroupUpdateIsConsulting = useSelector(
    (state) => state.professionGroupUpdateIsConsulting
  );
  const {
    loading: loadingUpdateIsConsulting,
    error: errorUpdateIsConsulting,
    success: successUpdateIsConsulting,
  } = professionGroupUpdateIsConsulting;

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1);
  };

  // FILTERING
  const [isFilterActive, setIsFilterActive] = useState(false);

  const filterConfig = {
    professionGroups: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: "",
  });

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setIsFilterActive(true);
    setPage(1);
    localStorage.setItem(
      "filterSettings_professionGroups",
      JSON.stringify(filters)
    ); // Example for PatientList
    dispatch(
      listProfessionGroups(
        slug,
        searchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false, // Assuming `fetchAll` is false by default
        filters.status
      )
    );

    // console.log("filters", filters);
  };

  // SORTING
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSortChange = (column) => {
    const newSortOrder =
      sortField === column && sortOrder === "asc" ? "desc" : "asc";

    if (sortField !== column || sortOrder !== newSortOrder) {
      // Only dispatch the action if sorting parameters change
      console.log(`Sorting ${column} in ${newSortOrder} order`);

      setSortField(column);
      setSortOrder(newSortOrder);
    }
  };

  // SIDEPANELS
  const [sidePanelModalOpen, setSidePanelOpen] = useState(false);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);

  // FILTER INACTIVE
  // Function to filter services based on role
  const filterProfessionGroupsByRole = (professionGroups) => {
    if (isAdmin) {
      // Admins see all services
      return professionGroups;
    } else {
      // Non-admins see only active services
      return professionGroups.filter(
        (professionGroup) => professionGroup.status === "active"
      );
    }
  };

  // HANDLERS

  const deleteProfessionGroupHandler = (professionGroupId) => {
    console.log("click");
    dispatch(deleteProfessionGroup(slug, professionGroupId));

    if (professionGroups.length === 1) {
      setPage(page - 1);
    }
  };

  // USEEFFECT

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listProfessionGroups(
          slug,
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          false,
          filterParams.status
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    slug,
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    successUpdate,
    successUpdateStatus,
  ]);

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(
        t("toasts.professionGroupCreate"),
        "success",
        3000
      );
      closeSidePanelModal();

      dispatch({ type: PROFESSIONGROUP_CREATE_RESET });
    } else if (errorCreate) {
      toastComponent.showToast(
        t("toasts.professionGroupCreateError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCreate, errorCreate]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(
        t("toasts.professionGroupUpdate"),
        "success",
        3000
      );
      closeEditSidePanelModal();

      dispatch({ type: PROFESSIONGROUP_UPDATE_RESET });
    } else if (errorUpdate) {
      toastComponent.showToast(
        t("toasts.professionGroupUpdateError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdate, errorUpdate]);

  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(
        t("toasts.professionGroupDelete"),
        "success",
        3000
      );
    } else if (errorDelete) {
      toastComponent.showToast(
        t("toasts.professionGroupDeleteError"),
        "error",
        3000
      );
    }
    dispatch({ type: PROFESSIONGROUP_DELETE_RESET });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successDelete, errorDelete]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.professionGroupUpdateStatus"),
        "success",
        3000
      );
      dispatch({ type: PROFESSIONGROUP_UPDATE_STATUS_RESET });
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.professionGroupUpdateStatusError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdateStatus, errorUpdateStatus]);

  // SIDEPANEL HANDLERS

  const openSidePanelModal = () => {
    setSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const openEditSidePanelModal = () => {
    setEditSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeSidePanelModal = () => {
    setSidePanelOpen(false);
    setEditSidePanelOpen(false);
    setProfessionGroupId("");
    document.body.classList.remove("no-scroll");
  };
  const closeEditSidePanelModal = () => {
    setEditSidePanelOpen(false);
    setSidePanelOpen(false);
    setProfessionGroupId("");
    document.body.classList.remove("no-scroll");
  };

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.on;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.create;
  const hasAccessToEdit =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.update;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.delete;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    console.log("perms", perms);

    if (perms.professionsAndProfessionGroups?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.professionGroups")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching || !professionGroups;

  // LOGS

  // console.log("professionGroups", professionGroups);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.professionGroups")}</title>
        <meta
          name="description"
          content={t("pageDescription.professionGroups")}
        />
      </Helmet>

      {/* SIDEPANELS START */}
      {!isMobile ? (
        <SidePanel
          open={sidePanelModalOpen}
          close={closeSidePanelModal}
          title={t("professionGroup.createProfessionGroup")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateProfessionGroup close={closeSidePanelModal} />
            ) : (
              <UserNoAccess
                pageName={t("noAccess.professionGroupCreate")}
                p="0"
              />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={sidePanelModalOpen}
          onClose={closeSidePanelModal}
          title={t("professionGroup.createProfessionGroup")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateProfessionGroup close={closeSidePanelModal} />
          ) : (
            <UserNoAccess
              pageName={t("noAccess.professionGroupCreate")}
              p="0"
            />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editSidePanelOpen}
          close={closeEditSidePanelModal}
          title={t("professionGroup.editProfessionGroup")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditProfessionGroup
              hasEditAccess={hasAccessToEdit}
              close={closeEditSidePanelModal}
              professionGroupId={professionGroupId}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editSidePanelOpen}
          onClose={closeEditSidePanelModal}
          title={t("professionGroup.editProfessionGroup")}
          size="full"
        >
          <EditProfessionGroup
            hasEditAccess={hasAccessToEdit}
            close={closeEditSidePanelModal}
            professionGroupId={professionGroupId}
          />
        </DrawerNew>
      )}
      {/* SIDEPANELS END */}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.professionGroupDelete")}
          deleteTitle={t("professionGroup.deleteProfessionGroupTitle")}
          deleteName={professionGroupName}
          // warningLabel={t("serviceGroup.deleteWarning")}
          warningLabel={
            selectedProfessionCount > 0
              ? t("professionGroup.deleteWarningWithProfessions", {
                  count: selectedProfessionCount,
                })
              : t("professionGroup.deleteWarning")
          }
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          // confirmAction={() => {
          //   deleteProfessionGroupHandler(professionGroupId); // Use `userId` set from `MoreButtonLite`
          //   setShowDeleteConfirmation(false);
          // }}
          confirmAction={() => {
            if (selectedProfessionCount > 0) {
              dispatch(
                updateProfessionGroupStatus(slug, professionGroupId, "inactive")
              );
            } else {
              deleteProfessionGroupHandler(professionGroupId);
            }
            setShowDeleteConfirmation(false);
          }}
        />
      )}
      {/* MODALS END */}

      {/* MAIN START */}

      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 6rem", "0rem 0rem 6rem", "2rem 2rem 6rem"]}
        >
          <Box
            w="100%"
            h="100%"
            bg={whiteMode}
            overflow="hidden"
            borderRadius="2rem"
          >
            <PageHeader
              label={t("professionGroup.professionGroups")}
              buttonHandler={openSidePanelModal}
              buttonLabel={t("professionGroup.createProfessionGroup")}
              filter={
                <></>
                // <TreatmentPlansFilterPopover
                //   onFilterApply={handleFilterApply}
                //   isFilterActive={isFilterActive}
                //   setIsFilterActive={setIsFilterActive}
                //   filterStatusOptions="treatment"
                //   savedFiltersKey="filterSettings_treatmentPlans"
                //   resetFilter={resetFilter}
                //   onResetComplete={() => setResetFilter(false)}
                // />
              }
            />

            <SearchInput
              placeholder={t("professionGroup.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0.5rem 1.5rem", "0 2rem"]}
            />

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={professionGroups}
                      columns={columnsDesktop(
                        slug,
                        dispatch,
                        translate,
                        updateProfessionGroupStatus,
                        handleSortChange,
                        setProfessionGroupId,
                        setShowDeleteConfirmation,
                        setProfessionGroupName,
                        setSelectedProfessionCount,
                        hasAccessToEdit,
                        openEditSidePanelModal
                      )}
                      noItemsTitle={t("noItems.noProfessionGroups")}
                      noItemsDescription={t("noItems.noProfessionGroupsText")}
                      noItemsButtonLabel={t("noItems.noProfessionGroupsButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(professionGroupId) => {
                        setProfessionGroupId(professionGroupId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={professionGroups}
                      columns={columnsMobile(
                        slug,
                        dispatch,
                        translate,
                        updateProfessionGroupStatus,
                        handleSortChange,
                        setProfessionGroupId,
                        setShowDeleteConfirmation,
                        setProfessionGroupName,
                        setSelectedProfessionCount,
                        hasAccessToEdit,
                        openEditSidePanelModal
                      )}
                      noItemsTitle={t("noItems.noProfessionGroupsTitle")}
                      noItemsDescription={t(
                        "noItems.noProfessionGroupsDescription"
                      )}
                      noItemsButtonLabel={t("noItems.createProfessionGroup")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(professionGroupId) => {
                        setProfessionGroupId(professionGroupId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            )}
          </Box>
        </Flex>
      </Box>
      {/* MAIN END */}
    </>
  );
};

export default ProfessionGroupListNew;
